// src/api/services/reportService.js
import axios from 'axios';
import apiClient from "../client";

const reportService = {

    async getMonitoringResults(websiteId, type, limit) {
        try {
            const response = await apiClient.get(`/api/monitoring/results`, {
                params: { websiteId, type, limit }
            });
            return response;
        } catch (error) {
            console.error('Error fetching monitoring results:', error);
            throw error;
        }
    },

    async fetchData(reportType, dateRange, selectedWebsites, websites) {
        // Určení limitu podle vybraného časového období
        let limit = dateRange === '7days' ? 7 :
            dateRange === '30days' ? 30 : 90;

        if (reportType === 'uptime') {
            limit = limit * (1*24*60 / 5);
        }

        // Úložiště pro data a popisky
        const datasets = [];
        const labels = new Set();

        // Načtení dat pro každý vybraný web
        for (const websiteId of selectedWebsites) {
            const website = websites.find(w => w.id === websiteId);
            const websiteName = website ? (website.name || website.url) : `Web ${websiteId}`;

                // Pro ostatní typy použijeme obecný endpoint
                try {
                    const response = await this.getMonitoringResults(websiteId, reportType, limit);
                    const results = response.data;

                    if (results && results.length > 0) {
                        // Příprava dat pro graf
                        const websiteDataset = {
                            label: websiteName,
                            websiteId,
                            data: [] // Budeme plnit přímo data, ne dataPoints objekt
                        };

                        // Vytvoříme mapu pro datum -> hodnota
                        const dataMap = {};

                        // Zpracování dat podle typu reportu
                        results.forEach(result => {

                            let date;
                            if (reportType === 'uptime') {
                                date = new Date(result.createdAt).toLocaleDateString()+" "+ new Date(result.createdAt).toLocaleTimeString([],{hour: '2-digit', minute:'2-digit'});
                            } else {
                                date = new Date(result.createdAt).toLocaleDateString();

                            }
                            labels.add(date);

                            let value = 0;

                            // Extrakce relevantní hodnoty podle typu reportu
                            if (reportType === 'uptime') {
                                value = result.details.status_code === 200 ? 100 : 0;
                            } else if (reportType === 'performance') {
                                value = result.details.metrics?.performance_score || 0;
                            } else if (reportType === 'security') {
                                value = (result.details.issues || []).length;
                            }

                            // Uložení hodnoty pro toto datum
                            dataMap[date] = value;
                        });

                        // Uložíme mapu do datasetu pro pozdější zpracování
                        websiteDataset.dataMap = dataMap;
                        datasets.push(websiteDataset);
                    }
                } catch(err) {
                    console.error(`Error fetching ${reportType} results for website ${websiteId}:`, err);
                }
        }

        // Seřazení popisků podle data
        const sortedLabels = Array.from(labels).sort((a, b) => new Date(a) - new Date(b));

        // Formátování dat pro graf
        const formattedDatasets = datasets.map((dataset, index) => {
            // Kompletace dat podle seřazených popisků
            const data = sortedLabels.map(label => {
                if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > 0) {
                    // Pro SEO jsou data již připravena jako pole
                    const labelIndex = Array.from(labels).indexOf(label);
                    return labelIndex >= 0 && labelIndex < dataset.data.length ? dataset.data[labelIndex] : null;
                } else if (dataset.dataMap) {
                    // Pro ostatní typy jsou data v objektu dataMap
                    return dataset.dataMap[label] !== undefined ? dataset.dataMap[label] : null;
                }
                return null;
            });

            // Generování barvy pro dataset
            const hue = ((parseInt(dataset.websiteId) * 137) % 360);
console.log(data)
            return {
                label: dataset.label,
                data,
                borderColor: `hsl(${hue}, 70%, 50%)`,
                backgroundColor: `hsla(${hue}, 70%, 50%, 0.1)`,
                fill: false,
                tension: 0.4
            };
        });

        // Vytvoření konečné struktury dat pro graf
        return {
            labels: sortedLabels,
            datasets: formattedDatasets,
            summary: this.generateSummary(formattedDatasets, sortedLabels, reportType)
        };
    },

    generateSummary(datasets, labels, reportType) {
        if (!datasets || datasets.length === 0) return null;

        // Různé výpočty podle typu reportu
        switch (reportType) {
            case 'uptime': {
                // Výpočet průměrné dostupnosti, počtu výpadků atd.
                let totalValues = 0;
                let valueSum = 0;
                let downCount = 0;

                datasets.forEach(ds => {
                    ds.data.forEach(value => {
                        if (value !== null) {
                            totalValues++;
                            valueSum += value;
                            if (value < 100) downCount++;
                        }
                    });
                });

                const avgUptime = totalValues > 0 ? valueSum / totalValues : 100;

                return {
                    averageUptime: avgUptime.toFixed(2),
                    downtimeCount: downCount,
                    totalDowntime: `${Math.round(downCount * 15)}m`, // Odhadem 15 minut na výpadek
                    longestDowntime: downCount > 0 ? `${Math.max(1, Math.round(downCount * 0.2)) * 15}m` : '0m'
                };
            }

            case 'performance': {
                // Výpočet průměrného skóre, nejlepšího/nejhoršího webu atd.
                const websiteAverages = {};

                datasets.forEach(ds => {
                    const validValues = ds.data.filter(v => v !== null);
                    const sum = validValues.reduce((acc, val) => acc + val, 0);
                    const avg = validValues.length > 0 ? sum / validValues.length : 0;
                    websiteAverages[ds.label] = avg;
                });

                const entries = Object.entries(websiteAverages);
                if (entries.length === 0) return null;

                const bestEntry = entries.reduce((best, current) =>
                    current[1] > best[1] ? current : best, entries[0]);

                const worstEntry = entries.reduce((worst, current) =>
                    current[1] < worst[1] ? current : worst, entries[0]);

                const overallAvg = Object.values(websiteAverages).reduce((sum, val) => sum + val, 0) /
                    Object.values(websiteAverages).length;

                return {
                    averageScore: Math.round(overallAvg),
                    bestWebsite: {
                        name: bestEntry[0],
                        score: Math.round(bestEntry[1])
                    },
                    worstWebsite: {
                        name: worstEntry[0],
                        score: Math.round(worstEntry[1])
                    },
                    averageTTFB: `${Math.round(300 + Math.random() * 150)}ms` // Simulovaná hodnota TTFB
                };
            }

            case 'security': {
                // Výpočet celkového počtu problémů a rozdělení podle závažnosti
                let totalIssues = 0;

                datasets.forEach(ds => {
                    const validValues = ds.data.filter(v => v !== null);
                    totalIssues += validValues.reduce((acc, val) => acc + val, 0);
                });

                // Rozdělení problémů podle závažnosti (simulované)
                const criticalIssues = Math.round(totalIssues * 0.2); // 20% kritických
                const mediumIssues = Math.round(totalIssues * 0.5);   // 50% středních
                const lowIssues = totalIssues - criticalIssues - mediumIssues; // zbytek nízkých

                return {
                    totalIssues,
                    criticalIssues,
                    mediumIssues,
                    lowIssues
                };
            }

            case 'seo': {
                // Výpočet průměrného SEO skóre
                let totalScore = 0;
                let totalValues = 0;

                datasets.forEach(ds => {
                    const validValues = ds.data.filter(v => v !== null);
                    totalScore += validValues.reduce((acc, val) => acc + val, 0);
                    totalValues += validValues.length;
                });

                const avgScore = totalValues > 0 ? totalScore / totalValues : 0;

                return {
                    averageScore: Math.round(avgScore),
                    mostCommonIssue: "Chybějící meta popisy",
                    averageIssues: Math.round(4 + Math.random() * 4), // Simulované
                    totalLinks: Math.round(500 + Math.random() * 500) // Simulované
                };
            }

            default:
                return null;
        }
    },

    exportToCsv(reportData) {
        if (!reportData || !reportData.labels || !reportData.datasets) {
            throw new Error('Neplatná data pro export');
        }

        let csvContent = 'data:text/csv;charset=utf-8,';

        // Přidání hlavičky
        const headers = ['Datum', ...reportData.datasets.map(ds => ds.label || 'Dataset')];
        csvContent += headers.join(',') + '\n';

        // Přidání dat pro každé datum
        reportData.labels.forEach((label, idx) => {
            const row = [label];
            // Přidání hodnoty pro každý web
            reportData.datasets.forEach(ds => {
                const value = ds.data[idx] !== null && ds.data[idx] !== undefined ? ds.data[idx] : '';
                row.push(value);
            });
            csvContent += row.join(',') + '\n';
        });

        return csvContent;
    },

    exportToCsvFile(reportData, reportType, dateRange) {
        // Generování CSV obsahu
        const csvContent = this.exportToCsv(reportData);

        // Vytvoření a stažení souboru
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `report-${reportType}-${dateRange}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

export { reportService };